import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Fade } from "react-awesome-reveal";
import { NavBar } from "../../components/navbar";
import { trackEvent } from "../../helpers";
import guideHero from "../../assets/guide-page-1.png";
import {
  PageWrapper,
  Wrap,
  TyHero,
  TyEb,
  TyHeroH1,
  TySub,
  TyHeroPicture,
  TySteps,
  TyStep,
  TyStepN,
  TyStepH2,
  TyStepP,
  TyTips,
  TyTip,
  TyTipLab,
  TyTipH3,
  TyTipP,
  TyFoot,
  TyFootWrap,
  LoadingState,
  Spinner,
} from "./success.styles";

export const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isMenuIconClicked, setIsMenuIconClicked] = useState(false);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    // Simulate verification delay
    // In production, you could verify the session with your backend
    const timer = setTimeout(() => {
      setIsVerifying(false);

      // Track successful checkout completion
      trackEvent('checkout_completed', {
        product: 'The Unhinged Guide to Breaking Into Tech',
        sessionId: sessionId || 'unknown',
        price: 39,
        currency: 'GBP'
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [sessionId]);

  if (isVerifying) {
    return (
      <>
        <Helmet>
          <title>Thank you · Renée Louise</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <NavBar
          isValidRoute={true}
          isResumePage={false}
          isMobile={false}
          isMenuIconClicked={isMenuIconClicked}
          setIsMenuIconClicked={setIsMenuIconClicked}
          variant="dark"
        />
        <PageWrapper>
          <LoadingState>
            <Spinner />
            <p>Verifying your purchase...</p>
          </LoadingState>
        </PageWrapper>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Thank you · Renée Louise</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <NavBar
        isValidRoute={true}
        isResumePage={false}
        isMobile={false}
        isMenuIconClicked={isMenuIconClicked}
        setIsMenuIconClicked={setIsMenuIconClicked}
        variant="dark"
      />
      <PageWrapper>
        <main>
          <Wrap>
            <Fade triggerOnce>
              <TyHero>
                <div>
                  <TyEb>Order confirmed</TyEb>
                  <TyHeroH1>
                    Congratulations on investing in <em>your next move.</em>
                  </TyHeroH1>
                  <TySub>
                    The Unhinged Guide to Breaking Into Tech and its toolkit are officially yours, ready to help you put the plan into action.
                  </TySub>
                </div>
                <TyHeroPicture>
                  <img
                    src={guideHero}
                    width="520"
                    height="664"
                    alt="The Unhinged Guide to Breaking Into Tech and its toolkit"
                  />
                </TyHeroPicture>
              </TyHero>
            </Fade>
          </Wrap>

          <Wrap>
            <Fade triggerOnce>
              <TySteps>
                <TyStep>
                  <TyStepN>01 · Now</TyStepN>
                  <TyStepH2>Check your inbox</TyStepH2>
                  <TyStepP>
                    Your download link is on its way. If it isn't there in a few minutes, check your <b>spam or promotions</b> folder.
                  </TyStepP>
                </TyStep>
                <TyStep>
                  <TyStepN>02 · Download</TyStepN>
                  <TyStepH2>Save your files</TyStepH2>
                  <TyStepP>
                    Click the link in the email and save everything somewhere you'll find it. The link stays valid for <b>90 days</b>.
                  </TyStepP>
                </TyStep>
                <TyStep>
                  <TyStepN>03 · Start</TyStepN>
                  <TyStepH2>Begin with Chapter 1</TyStepH2>
                  <TyStepP>
                    Read <b>Why the Hell Do You Want to Work in Tech?</b>, then complete Tool 01, the Find Your Why Worksheet.
                  </TyStepP>
                </TyStep>
              </TySteps>
            </Fade>
          </Wrap>

          <Wrap>
            <Fade triggerOnce>
              <TyTips>
                <TyTip>
                  <TyTipLab>Using the toolkit</TyTipLab>
                  <TyTipH3>Type on screen, or print</TyTipH3>
                  <TyTipP>
                    Open the fillable toolkit in Adobe Acrobat Reader, Chrome or Preview and type straight into the worksheets. Use <b>Save</b>, not Print to PDF, to keep your answers.
                  </TyTipP>
                </TyTip>
                <TyTip>
                  <TyTipLab>Need help?</TyTipLab>
                  <TyTipH3>No email, or a broken link?</TyTipH3>
                  <TyTipP>
                    Email <a href="mailto:hello@reneelouise.tech">hello@reneelouise.tech</a> with the address you used at checkout and I'll sort it.
                  </TyTipP>
                </TyTip>
                <TyTip>
                  <TyTipLab>Refund policy</TyTipLab>
                  <TyTipH3>No refunds</TyTipH3>
                  <TyTipP>
                    Because you get instant access to all digital files, we don't offer refunds. Make sure this is right for you before purchasing.
                  </TyTipP>
                </TyTip>
              </TyTips>
            </Fade>
          </Wrap>
        </main>

        <TyFoot>
          <TyFootWrap>
            <Wrap>
              <TyFootWrap>
                <span>© 2026 Renée Louise</span>
                <span>Now go do the thing.</span>
              </TyFootWrap>
            </Wrap>
          </TyFootWrap>
        </TyFoot>
      </PageWrapper>
    </>
  );
};
